import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Manages focus on route changes for accessibility.
 * Moves focus to the main content area after navigation so screen readers
 * announce the new page context.
 */
export function useRouteFocus() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip focus management on initial page load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Focus the main content area after route change
    const main = document.getElementById('main-content');
    if (main) {
      main.focus({ preventScroll: false });
    }
  }, [location.pathname]);
}

/**
 * Traps focus within a container element while active.
 * Used for modals and dialogs to prevent focus from escaping.
 */
export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    // Store the element that had focus before the trap activated
    previousFocusRef.current = document.activeElement as HTMLElement;

    const container = containerRef.current;
    if (!container) return;

    // Focus the first focusable element inside the container
    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const firstFocusable = container.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !container) return;

      const focusableElements = container.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Return focus to the element that triggered the modal
      previousFocusRef.current?.focus();
    };
  }, [active]);

  return containerRef;
}

/**
 * Hook for announcing dynamic content changes to screen readers.
 * Returns a ref to attach to an aria-live region and a function to announce messages.
 */
export function useAnnouncer() {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const el = document.getElementById(`aria-live-${priority}`);
    if (el) {
      // Clear then set to force re-announcement
      el.textContent = '';
      requestAnimationFrame(() => {
        el.textContent = message;
      });
    }
  };

  return { announce };
}
